/* jshint esversion: 6 */

/**
 * CMSMediaInfoService is a service class that provides methods to interact with media information within a CMS context.
 * It fetches and manipulates media-related data using various services and configurations.
 */
class CMSMediaInfoService {
    /**
     * Constructs a new instance of CMSMediaInfoService.
     * @param {Object} TaskDataService - The service responsible for fetching task-related data.
     * @param {Object} $config - The configuration service providing lists of advanced object types and special container types.
     * @param {Object} Media - The media object used within the CMS.
     */
    constructor(TaskDataService, $config, Media) {
        // 'ngInject'; // Remove this line if present
        this._taskContext = TaskDataService.fetchContext();
        this._availableAdvancedObjectTypes = $config.getAdvancedObjectTypesList();
        this._availableSpecialContainerTypes = $config.getSpecialObjectContainerList();
        this._availableMediaTypes = this.getAvailableMediaTypes();
        this._mediaContainersCache = undefined;
    }

    /**
     * Clears the cache of media containers.
     */
    clearCache() {
        this._mediaContainersCache = undefined;
    }

    /**
     * Retrieves instances of a specific type from the task context.
     * @param {string} instanceType - The type of instances to retrieve.
     * @returns {Object} - A collection of instances of the specified type.
     */
    getInstancesOfType(instanceType) {
        return this._taskContext.findInstancesByType(instanceType);
    }

    /**
     * Retrieves a list of all instance types available in the task context.
     * @returns {Array} - An array of instance types.
     */
    getInstanceTypes() {
        return this._taskContext.getListOfInstanceTypes();
    }

    /**
     * Retrieves all media instances from the task context.
     * @returns {Object} - A collection of media instances.
     */
    getMediaInstances() {
        const instances = this.getInstancesOfType('media');
        return instances || {};
    }

    /**
     * Retrieves media instances filtered by a specific media type.
     * @param {string} mediaType - The type of media to filter by.
     * @returns {Object|boolean} - A collection of media instances of the specified type, or false if the media type is undefined.
     */
    getMediaInstancesByType(mediaType) {
        if (typeof mediaType === 'undefined') {
            console.warn('media type is missing, please enter one of the following types: %s', this.getAvailableMediaTypes().join(','));
            return false;
        }
        var mediaList = this.getInstancesOfType('media'),
            instances = {};
        for (var uuid in mediaList) {
            if (mediaList.hasOwnProperty(uuid) && mediaList[uuid].getFileType() === mediaType) {
                instances[uuid] = this._taskContext.read(uuid);
            }
        }
        return instances;
    }

    /**
     * Retrieves a list of all available media types from the media instances.
     * @returns {Array} - An array of available media types.
     */
    getAvailableMediaTypes() {
        const mediaList = this.getMediaInstances();
        const list = [];
        
        // Convert object to array if necessary
        const mediaArray = Object.values(mediaList);
        
        mediaArray.forEach(media => {
            const mtype = media.getFileType();
            if (list.indexOf(mtype) === -1) {
                list.push(mtype);
            }
        });
        
        return list;
    }

    /**
     * Retrieves media information from a content object.
     * @param {Object} contentObject - The content object to extract media information from.
     * @returns {Object} - An object containing media UUID and type, or an empty object if the content object does not contain media.
     */
    getMediaFromContentObject(contentObject) {
        if (contentObject.hasOwnProperty('getContentReference') &&
            contentObject.getContentReference() &&
            contentObject.getObjectType()) {

            return {
                mediaUUID: contentObject.getContentReference().getUUID(),
                mediaType: contentObject.getObjectType()
            };
        }
        return {};
    }

    /**
     * Retrieves media information from an advanced content object.
     * @param {Object} contentObject - The advanced content object to extract media information from.
     * @returns {Object} - An object containing media UUID and type, or an empty object if the content object does not contain media.
     */
    getMediaFromAdvancedObject(contentObject) {
        if (contentObject.hasOwnProperty('getContentReference') &&
            !contentObject.getContentReference() &&
            contentObject.hasOwnProperty('getPreferences') &&
            contentObject.getPreferences().mediaContent) {

            return {
                mediaUUID: contentObject.getPreferences().mediaContent,
                mediaType: contentObject.getObjectType()
            };
        }
        return {};
    }

    /**
     * Retrieves objects that reference media.
     * @returns {Object} - A collection of objects that reference media.
     */
    getObjectsWithReferencedMedia() {
        var _contentObjects = this.getInstancesOfType('contentObject'),
            _instances = {};

        for (var uuid in _contentObjects) {
            if (_contentObjects.hasOwnProperty(uuid)) {
                var tmp = this.getMediaFromContentObject(_contentObjects[uuid]);
                if (Object.keys(tmp).length) {
                    if (!(_instances[uuid] instanceof Array)) {
                        _instances[uuid] = [];
                    }
                    _instances[uuid].push(tmp);
                }
            }
        }
        return _instances;
    }

    /**
     * Retrieves advanced objects that reference media.
     * @returns {Object} - A collection of advanced objects that reference media.
     */
    getAdvancedObjectsWithReferencedMedia() {
        var _contentObjects = this.getInstancesOfType('contentObject'),
            _instances = {};

        for (var uuid in _contentObjects) {
            if (_contentObjects.hasOwnProperty(uuid)) {
                var tmp = this.getMediaFromAdvancedObject(_contentObjects[uuid]);
                if (Object.keys(tmp).length) {
                    if (!(_instances[uuid] instanceof Array)) {
                        _instances[uuid] = [];
                    }
                    _instances[uuid].push(tmp);
                }
            }
        }
        return _instances;
    }

    /**
     * Retrieves special containers that reference media.
     * @param {string} containerType - The type of special container to filter by.
     * @returns {Object|boolean} - A collection of special containers that reference media, or false if the container type is invalid.
     */
    getSpecialContainersWithReferencedMedia(containerType) {
        var _containers = this.getInstancesOfType('container'),
            _instances = {},
            _specialContainer;

        if (this._availableSpecialContainerTypes.indexOf(containerType) === -1) {
            console.warn('%cPlease use "tabs" or "carousels" as parameter', 'color:red');
            return false;
        } else if (containerType === 'tabs') {
            _specialContainer = 'getTabs';
        } else if (containerType === 'carousels') {
            _specialContainer = 'getCarousels';
        }

        for (var uuid in _containers) {
            if (_containers.hasOwnProperty(uuid) && _containers[uuid].hasOwnProperty(_specialContainer) &&
                _containers[uuid][_specialContainer]()) {

                var _sContainers = _containers[uuid][_specialContainer]();
                for (var idx in _sContainers) {
                    if (_sContainers.hasOwnProperty(idx)) {
                        var tab = this._taskContext.read(_sContainers[idx].content);

                        if (tab.hasOwnProperty('getObjects') && tab.getObjects()) {
                            var objects = tab.getObjects();
                            for (var idxo in objects) {
                                if (objects.hasOwnProperty(idxo)) {
                                    var obj = this._taskContext.read(objects[idxo]),
                                        tmp = this.getMediaFromContentObject(obj);

                                    if (Object.keys(tmp).length) {
                                        if (!(_instances[uuid] instanceof Array)) {
                                            _instances[uuid] = [];
                                        }
                                        _instances[uuid].push(tmp);
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
        return _instances;
    }

    /**
     * Retrieves media containers and caches the result.
     * @returns {Object} - A collection of media containers.
     */
    getMediaContainers() {
        var containerList = this._taskContext.findInstancesByType('container'),
            _mediaContainers = {};

        for (var container in containerList) {
            if (containerList.hasOwnProperty(container) && typeof containerList[container] === 'object') {
                if ('getObjects' in containerList[container] && containerList[container].getObjects()) {
                    var objs = containerList[container].getObjects();
                    if (!objs) continue;
                    if (typeof objs !== 'undefined' && objs instanceof Array) {
                        for (var i = 0; i < objs.length; i++) {
                            if (typeof objs[i] !== "string") {
                                continue;
                            }
                            var tmp = this._taskContext.read(objs[i]);
                            if (typeof tmp === 'undefined' || !tmp) {
                                continue;
                            }
                            if (!('getContentReference' in tmp)) {
                                continue;
                            }
                            if (tmp.getObjectType() && tmp.getContentReference() && this._availableMediaTypes.indexOf(tmp.getObjectType()) !== -1) {
                                if (tmp.getContentReference()) {
                                    if ('getUUID' in tmp.getContentReference()) {
                                        var mediaUuid = tmp.getContentReference().getUUID();
                                        if (mediaUuid) {
                                            if (!(_mediaContainers[mediaUuid] instanceof Array)) {
                                                _mediaContainers[mediaUuid] = [];
                                            }
                                            if (_mediaContainers[mediaUuid].indexOf(container) === -1) {
                                                _mediaContainers[mediaUuid].push(container);
                                            }
                                        }
                                    }
                                }
                            } else if (tmp.getObjectType() && this._availableAdvancedObjectTypes.indexOf(tmp.getObjectType()) !== -1) {
                                var advUuid = tmp.getPreferences().mediaContent;
                                if (advUuid) {
                                    if (!(_mediaContainers[advUuid] instanceof Array)) {
                                        _mediaContainers[advUuid] = [];
                                    }
                                    if (_mediaContainers[advUuid].indexOf(container) === -1) {
                                        _mediaContainers[advUuid].push(container);
                                    }
                                }
                            }
                        }
                    }
                } else if (containerList[container].isShowTabs()) {
                    var tabs = containerList[container].getTabs();
                    for (var j = 0; j < tabs.length; j++) {
                        var tabObjs = this._taskContext.read(tabs[j].content).getObjects();
                        if (!tabObjs) continue;
                        for (var k = 0; k < tabObjs.length; k++) {
                            var tabTmp = this._taskContext.read(tabObjs[k]);
                            if (!tabTmp) {
                                continue;
                            }
                            if (tabTmp.getObjectType() && this._availableMediaTypes.indexOf(tabTmp.getObjectType()) !== -1) {
                                if (tabTmp.getContentReference() && 'getUUID' in tabTmp.getContentReference()) {
                                    var tabUuid = tabTmp.getContentReference().getUUID();
                                    if (tabUuid) {
                                        if (!(_mediaContainers[tabUuid] instanceof Array)) {
                                            _mediaContainers[tabUuid] = [];
                                        }
                                        if (_mediaContainers[tabUuid].indexOf(container) === -1) {
                                            _mediaContainers[tabUuid].push(container);
                                        }
                                    }
                                }
                            }
                        }
                    }
                } else if (containerList[container].isShowCarousels()) {
                    var carousels = containerList[container].getCarousels();
                    for (var m = 0; m < carousels.length; m++) {
                        var carouselObjs = this._taskContext.read(carousels[m].content).getObjects();
                        if (!carouselObjs) continue;
                        for (var n = 0; n < carouselObjs.length; n++) {
                            var carouselTmp = this._taskContext.read(carouselObjs[n]);
                            if (!carouselTmp) {
                                continue;
                            }
                            if (carouselTmp.getObjectType() && this._availableMediaTypes.indexOf(carouselTmp.getObjectType()) !== -1) {
                                if (carouselTmp.getContentReference() && 'getUUID' in carouselTmp.getContentReference()) {
                                    var carouselUuid = carouselTmp.getContentReference().getUUID();
                                    if (carouselUuid) {
                                        if (!(_mediaContainers[carouselUuid] instanceof Array)) {
                                            _mediaContainers[carouselUuid] = [];
                                        }
                                        if (_mediaContainers[carouselUuid].indexOf(container) === -1) {
                                            _mediaContainers[carouselUuid].push(container);
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
        this._mediaContainersCache = _mediaContainers;
        return _mediaContainers;
    }

    /**
     * Retrieves content objects filtered by a specific object type.
     * @param {string} objectType - The type of content objects to filter by.
     * @returns {Object} - A collection of content objects of the specified type.
     */
    getContentObjectsByObjectType(objectType) {
        var _contentObjects = this.getInstancesOfType('contentObject'),
            _instances = {};

        for (var uuid in _contentObjects) {
            if (_contentObjects.hasOwnProperty(uuid) && _contentObjects[uuid].hasOwnProperty('getObjectType') && _contentObjects[uuid].getObjectType() === objectType) {
                _instances[uuid] = this._taskContext.read(uuid);
            }
        }
        return _instances;
    }

    /**
     * Debug method to retrieve and log all objects.
     */
    debugGetAllObject() {
        var containerList = this._taskContext.findInstancesByType('container'),
            _mediaContainers = {};

        for (var container in containerList) {
            if (containerList.hasOwnProperty(container) && typeof containerList[container] === 'object') {
                console.warn(container, containerList[container]);
                if ('getObjects' in containerList[container] && containerList[container].getObjects()) {
                    var objs = containerList[container].getObjects();
                    console.info('  getObjects', objs);
                    if (typeof objs !== 'undefined' && objs instanceof Array) {
                        for (var i = 0; i < objs.length; i++) {
                            try {
                                if (typeof objs[i] !== "string") {
                                    throw "String expected";
                                }
                                console.log('    [%s]', i, objs[i]);
                                var tmp = this._taskContext.read(objs[i]);
                                if (typeof tmp === 'undefined' || !tmp) {
                                    continue;
                                }
                                if (!('getContentReference' in tmp)) {
                                    continue;
                                }
                            } catch (err) {
                                console.error(err.trim(), ' got "' + typeof objs[i] + '":', objs[i]);
                                return;
                            }
                        }
                    }
                }
            }
        }
    }

    /**
     * Checks if a given UUID corresponds to a media type.
     * @param {string} uuid - The UUID to check.
     * @returns {boolean} - True if the UUID corresponds to a media type, false otherwise.
     */
    isMediaType(uuid) {
        if (typeof uuid === 'undefined') {
            console.warn('Object\'s uuid is missing');
            return false;
        }
        return this._taskContext.read(uuid).getType() === 'media';
    }
}

// Add explicit dependency injection
CMSMediaInfoService.$inject = ['TaskDataService', '$config', 'Media'];

export default CMSMediaInfoService;